import React, { useEffect, useMemo, useState } from 'react';
import { listItemReservations, mapToCalendarEvent } from '../../utils/reservationOperations';

// Lightweight calendar: simple agenda/week grid without external deps
const dayMs = 24 * 60 * 60 * 1000;

const startOfWeek = (d) => {
  const date = new Date(d);
  const diff = date.getDay(); // 0..6, assuming Sunday start
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setTime(start.getTime() - diff * dayMs);
  return start;
};

const endOfWeek = (d) => {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setTime(start.getTime() + 7 * dayMs - 1);
  return end;
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const ItemReservationsCalendar = ({ itemId, itemName }) => {
  const [cursorDate, setCursorDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const range = useMemo(() => ({ from: startOfWeek(cursorDate), to: endOfWeek(cursorDate) }), [cursorDate]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const reservations = await listItemReservations(itemId, range);
        const mapped = reservations
          .filter(r => r.status !== 'canceled')
          .map(r => mapToCalendarEvent(r, itemName));
        setEvents(mapped);
      } catch (e) {
        setError(e.message || 'Failed to load reservations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [itemId, itemName, range]);

  const days = useMemo(() => {
    const start = startOfWeek(cursorDate);
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(start);
      d.setTime(start.getTime() + i * dayMs);
      return d;
    });
  }, [cursorDate]);

  const eventsByDay = useMemo(() => {
    const map = {};
    days.forEach(d => { map[d.toDateString()] = []; });
    events.forEach(ev => {
      // Add to any day the event overlaps
      days.forEach(d => {
        const startDay = new Date(d); startDay.setHours(0,0,0,0);
        const endDay = new Date(d); endDay.setHours(23,59,59,999);
        if (ev.end > startDay && ev.start < endDay) {
          map[d.toDateString()].push(ev);
        }
      });
    });
    // sort by start
    Object.keys(map).forEach(k => map[k].sort((a,b) => a.start - b.start));
    return map;
  }, [events, days]);

  return (
    <div className="mt-8">
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-3">This Week's Reservations</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-stretch sm:items-center">
          <div className="flex gap-2 flex-1">
            <button
              onClick={() => setCursorDate(new Date(cursorDate.getTime() - 7 * dayMs))}
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium text-center"
            >
              ◀ Previous Week
            </button>
            <button
              onClick={() => setCursorDate(new Date(cursorDate.getTime() + 7 * dayMs))}
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium text-center"
            >
              Next Week ▶
            </button>
          </div>
          <button
            onClick={() => setCursorDate(new Date())}
            className="px-4 py-3 sm:py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 font-medium"
          >
            Today
          </button>
        </div>
      </div>
      {loading && <p className="text-gray-600">Loading calendar...</p>}
      {error && <p className="text-red-600">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d) => (
          <div key={d.toISOString()} className={`border rounded p-2 ${sameDay(d, new Date()) ? 'border-yellow-500' : 'border-gray-200'}`}>
            <div className="text-sm font-medium mb-2">
              {d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            <div className="space-y-2">
              {eventsByDay[d.toDateString()].length === 0 && (
                <div className="text-xs text-gray-400">No reservations</div>
              )}
              {eventsByDay[d.toDateString()].map(ev => (
                <div key={ev.id + d.toDateString()} className="text-xs border rounded p-1 bg-yellow-50 border-yellow-200">
                  <div className="font-semibold truncate">{ev.title}</div>
                  <div>{ev.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {ev.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  {ev.pid && <div>PID: {ev.pid}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ItemReservationsCalendar;


